import {
  confirmDialog,
  CustomButton,
  CustomCollapseCard,
  CustomTable,
  successDialog,
  Text,
  useText,
} from '@src/components';
import React, { useCallback, useEffect, useState } from 'react';
import '@src/styles/pendingApproval/balanceOrder.scss';
import { DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getAutoLoadingApprovalPagedList,
  instructionDelete,
} from '@src/store/slice/autoUploadInstructionsSlice';
import dayjs from '@src/utils/day';
import OrderSummaryBalanceModal from '../ticketRestaurant/myOrders/OrderSummaryBalanceModal';
import { setSelectedOrder } from '@src/store/slice/orderSlice';
import { moneyFormat } from '@src/utils/utils';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) =>
    expanded ? (
      <UpCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    ) : (
      <DownCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    );
}

function getExpandedRowRender(handleCancelAutoUpload, handleApprovedAutoUpload) {
  return (record) => (
    <div className="custom-expanded-row" key={record.id}>
      <div className="colum-title">
        <span>
          <Text t="transactionDate" />
        </span>{' '}
        <span>{record.processDate}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="numberOfCards" />
        </span>{' '}
        <span>{record.totalCount}</span>
      </div>
      <div className="colum-title">
        <span>SİPARİŞ TUTARI:</span> <span>{record.totalPrice} TL</span>
      </div>

      <div className="mobile-action-btn">
        <CustomButton
          id="pendingApproval-autoUploadInstructionsList-custom-btn-cancel"
          buttonAuthName="AwaitingApprovalCancel"
          onClick={() => handleCancelAutoUpload(record)}
          className="custom-btn-cancel"
        >
          <Text t="cancel2" />
        </CustomButton>
        <CustomButton
          id="pendingApproval-autoUploadInstructionsList-custom-btn-approved"
          buttonAuthName="ApprovalAuthorizationGroup"
          onClick={() => handleApprovedAutoUpload(record)}
          className="custom-btn-approved"
        >
          <Text t="approve" />
        </CustomButton>
      </div>
    </div>
  );
}

const AutoUploadInstructionsList = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { pendingApprovalList, detailTableProperty } = useSelector(
    (state) => state?.autoUploadInstructions,
  );
  const { processType } = useSelector((state) => state?.packages);
  const { selectedCustomer } = useSelector((state) => state?.customer);

  const [expandedRows, setExpandedRows] = useState([]);
  const [orderSummaryBalanceModalVisible, setOrderSummaryBalanceModalVisible] = useState(false);

  // 1. Move hook calls to the top level of the component
  const giveUpCancellationText = useText('giveUpCancellationText');
  const yesText = useText('yes');
  const noText = useText('no');
  const attentionText = useText('attention');
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

  useEffect(() => {
    if (processType === 3 || processType === 0) loadPendingApprovalList();
  }, [processType]);

  useEffect(() => {
    getList();
  }, []);

  const getList = async () => {
    const body = {
      customerId: selectedCustomer?.customerId,
      vouId: 1,
      pageNumber: 1,
      pageSize: 10,
    };
    await dispatch(getAutoLoadingApprovalPagedList(body));
  };

  const loadPendingApprovalList = useCallback(() => {
    getList();
  }, [dispatch, selectedCustomer?.customerId]);

  const handlePendingApprovalTableChange = useCallback(
    (pageNumber, pageSize) => {
      const body = {
        customerId: selectedCustomer?.customerId,
        vouId: 1,
        pageNumber,
        pageSize,
      };
      dispatch(getAutoLoadingApprovalPagedList(body));
    },
    [dispatch, selectedCustomer?.customerId],
  );

  const handleApprovedAutoUpload = async (item) => {
    await history.push(
      `/ticket-restaurant/auto-upload-instructions/confirm-instruction?instructionId=${item.id}`,
    );
  };

  const handleCancelAutoUpload = async (record) => {
    const body = {
      id: record?.id,
    };
    confirmDialog({
      title: <Text t={attentionText} />,
      htmlContent: <Text t={giveUpCancellationText} />,
      okText: <Text t={yesText} />,
      cancelText: <Text t={noText} />,
      onOk: async () => {
        const action = await dispatch(instructionDelete(body));

        if (instructionDelete.fulfilled.match(action)) {
          await getList();
          successDialog({
            title: <Text t="success" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: async () => {
              loadPendingApprovalList();
            },
          });
        }
      },
    });
  };

  const handleAutoBalanceDetail = (record) => {
    dispatch(setSelectedOrder(record));
    setOrderSummaryBalanceModalVisible(true);
  };

  const columnsAutoUpload = [
    {
      title: <Text t="orderNoUpperCase" />,
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      render: (text, record) => {
        return (
          <div
            className="balance-order-list-order-number"
            onClick={() => handleAutoBalanceDetail(record)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: <Text t="actionBy" />,
      dataIndex: 'createUser',
      key: 'createUser',
      width: '20%',
    },
    {
      title: <Text t="transactionDate2" />,
      dataIndex: 'processDate',
      key: 'processDate',
      width: '20%',
      responsive: ['md'],
      render: (text) => {
        return <div>{text && dayjs(text).format('DD/MM/YYYY')}</div>;
      },
    },
    {
      title: <Text t="numberOfCard3" />,
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: '20%',
      responsive: ['md'],
    },
    {
      title: <Text t="orderPrice" />,
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: '20%',
      responsive: ['md'],
      render: (text) => <span>{text ? `${moneyFormat(text, false)} TL` : '0'}</span>,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: '40%',
      responsive: ['md'],
      render: (_, record) => {
        return (
          <div className="row-btn-actions">
            <CustomButton
              onClick={() => handleCancelAutoUpload(record)}
              className="custom-btn-cancel"
              buttonAuthName="AwaitingApprovalCancel"
            >
              <Text t="cancel2" />
            </CustomButton>
            <CustomButton
              onClick={() => handleApprovedAutoUpload(record)}
              className="custom-btn-approved"
              buttonAuthName="ApprovalAuthorizationGroup"
            >
              <Text t="approve" />
            </CustomButton>
          </div>
        );
      },
    },
  ];

  return (
    <CustomCollapseCard
      className="auto-upload-card"
      cardTitle={<Text t="automaticInstallationInstruction" />}
    >
      <CustomTable
        pagination={
          detailTableProperty?.totalCount > 10
            ? {
              total: detailTableProperty?.totalCount,
              pageSize: detailTableProperty?.pageSize,
              current: detailTableProperty?.currentPage,
              onChange: (pageNumber, pageSize) =>
                handlePendingApprovalTableChange(pageNumber, pageSize),
              defaultCurrent: 1,
            }
            : false
        }
        isScroll={false}
        dataSource={pendingApprovalList}
        columns={columnsAutoUpload}
        rowKey={(record) => `list-${record?.id}`}
        expandedRowKeys={expandedRows}
        expandable={{
          showExpandColumn: false,
          expandIconColumnIndex: 7,
          onExpand: (expanded, record, key) =>
            expandedRows === key ? setExpandedRows(null) : setExpandedRows(key),
          expandIcon: getExpandIcon(),
          expandedRowRender: getExpandedRowRender(handleCancelAutoUpload, handleApprovedAutoUpload),
        }}
      />
      <OrderSummaryBalanceModal
        modalVisible={orderSummaryBalanceModalVisible}
        handleModalVisible={setOrderSummaryBalanceModalVisible}
        isAuto={true}
      />
    </CustomCollapseCard>
  );
};

export default AutoUploadInstructionsList;